import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "react-toastify";
import CommonService from "src/modules/data-protection/apis/CommonService";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import { CUSTOM_OPTION_VALUE } from "../helpers/constants";

const BASE_QUERY_OPTIONS = {
  cacheTime: 0,
  refetchOnWindowFocus: false,
};

const useSoftwareFormOptions = ({ enabled = true, organizationId, t }) => {
  const providersQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled,
    queryKey: ["software-creation-providers"],
    queryFn: async () =>
      DataProtectionSoftwareService.getInstance().providers(1000, 1),
    onError: () =>
      toast(t("An error occurred while fetching providers."), { type: "error" }),
  });

  const operatingSystemsQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled,
    queryKey: ["software-creation-operating-systems"],
    queryFn: async () =>
      DataProtectionSoftwareService.getInstance().operatingSystems(1000, 1),
    onError: () =>
      toast(t("An error occurred while fetching operating systems."), {
        type: "error",
      }),
  });

  const modulesQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled,
    queryKey: ["software-creation-modules"],
    queryFn: async () => DataProtectionSoftwareService.getInstance().modules(),
    onError: () =>
      toast(t("An error occurred while fetching modules."), { type: "error" }),
  });

  const countriesQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled,
    queryKey: ["software-creation-countries"],
    queryFn: async () => CommonService.getInstance().countries(),
    onError: () =>
      toast(t("An error occurred while fetching countries."), { type: "error" }),
  });

  const usersQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled,
    queryKey: ["software-creation-users"],
    queryFn: async () =>
      CommonService.getInstance().analystAdminsAndAnalysts(),
    onError: () =>
      toast(t("An error occurred while fetching users."), { type: "error" }),
  });

  const legalEntitiesQuery = useQuery({
    ...BASE_QUERY_OPTIONS,
    enabled: enabled && !!organizationId,
    queryKey: ["software-creation-legal-entities", organizationId],
    queryFn: async () => CommonService.getInstance().legalEntities(organizationId),
    onError: () =>
      toast(t("An error occurred while fetching legal entities."), {
        type: "error",
      }),
  });

  const providerOptions = useMemo(
    () => [
      { label: t("Custom"), value: CUSTOM_OPTION_VALUE },
      ...(providersQuery.data?.providers || []).map((provider) => ({
        label: provider.name,
        value: provider.id,
      })),
    ],
    [providersQuery.data, t]
  );

  const operatingSystemOptions = useMemo(
    () => [
      { label: t("Custom"), value: CUSTOM_OPTION_VALUE },
      ...(operatingSystemsQuery.data?.operationSystems || []).map((os) => ({
        label: os.name,
        value: os.name.trim().toLowerCase(),
      })),
    ],
    [operatingSystemsQuery.data, t]
  );

  const moduleOptions = useMemo(
    () =>
      (modulesQuery.data || []).map((module) => ({
        label: module.name,
        value: module.id,
      })),
    [modulesQuery.data]
  );

  const countryOptions = useMemo(
    () =>
      (countriesQuery.data || []).map((country) => ({
        label: country.name,
        value: country.id,
      })),
    [countriesQuery.data]
  );

  return {
    countryOptions,
    legalEntityOptions: legalEntitiesQuery.data || [],
    moduleOptions,
    operatingSystemOptions,
    providerOptions,
    users: usersQuery.data || [],
    isLoadingCountries: countriesQuery.isLoading || countriesQuery.isFetching,
    isLoadingLegalEntities:
      legalEntitiesQuery.isLoading || legalEntitiesQuery.isFetching,
    isLoadingModules: modulesQuery.isLoading || modulesQuery.isFetching,
    isLoadingOperatingSystems:
      operatingSystemsQuery.isLoading || operatingSystemsQuery.isFetching,
    isLoadingProviders: providersQuery.isLoading || providersQuery.isFetching,
  };
};

export { CUSTOM_OPTION_VALUE };
export default useSoftwareFormOptions;
